import Image from 'next/image';
import React, { FC, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Icon from '@wetransfer/transfer/component/icon';
import DIRECTORIES_SELECTION_STATES from '@wetransfer/transfer/constant/directories-selection-states';
import { getDirectoriesSelectionState } from '@wetransfer/transfer/helper/layout';
import I18n from '@wetransfer/transfer/modules/i18n';
import { allowedSizeSelector } from '@wetransfer/transfer/store/selector/transfers';
import { TransferItemAPITypes } from '@wetransfer/transfer/transfer-bundle/constant/transfer-item-types';
import { getCurrentSize } from '@wetransfer/transfer/transfer-bundle/store/selectors';
import avatar from '@wetransfer/transfer/public/assets/images/placeholder/avatar.png';
import { addFiles } from '@wetransfer/transfer/transfer-bundle/store/actions';
import { State } from '@wetransfer/transfer/store/types';

import styles from './TransferInUploader.module.scss';
import { TransferInUploaderProps } from './types';

// There's an ongoing bug about JSX not allowing directory and webkitdirectory
// Fix taken from here: https://github.com/facebook/react/issues/3468#issuecomment-1035152179
declare module 'react' {
  interface InputHTMLAttributes<T> extends HTMLAttributes<T> {
    directory?: string;
    webkitdirectory?: string;
  }
}

export const TransferInUploader: FC<TransferInUploaderProps> = ({
  channelName,
}) => {
  const dispatch = useDispatch();
  const filesInputRef = useRef<HTMLInputElement>(null);
  const directoriesInputRef = useRef<HTMLInputElement>(null);
  const directoriesSelectionState = getDirectoriesSelectionState();

  const { transfer, allowedSize, maximumNumberOfFiles, currentSize } =
    useSelector((state: State) => {
      const { transfer, user } = state;

      return {
        transfer,
        allowedSize: allowedSizeSelector(state),
        user,
        maximumNumberOfFiles: user.rules.maximumNumberOfFiles,
        currentSize: getCurrentSize(state),
      };
    });

  const triggerFilesSelection = (
    event:
      | React.MouseEvent<HTMLDivElement>
      | React.KeyboardEvent<HTMLDivElement>
  ) => {
    const folderUploadButton = document.querySelector(
      'button[data-type="folder"]'
    );

    // We don't want the file upload handler to be called when triggering
    // the folder upload with our keyboard
    if (event.target === folderUploadButton) {
      return;
    }

    if (!filesInputRef.current) {
      return;
    }

    filesInputRef.current.click();
  };

  const triggerDirectoriesSelection = (
    event:
      | React.MouseEvent<HTMLButtonElement>
      | React.KeyboardEvent<HTMLButtonElement>
  ) => {
    event.stopPropagation();

    if (!directoriesInputRef.current) {
      return;
    }

    directoriesInputRef.current.click();
  };

  const handleAddFiles = (
    event: React.MouseEvent<any> | React.KeyboardEvent<any>
  ) => {
    // We need this because Safari allows simultaneous uploads of files and folders
    if (directoriesSelectionState === DIRECTORIES_SELECTION_STATES.COMBINED) {
      triggerDirectoriesSelection(event);

      return;
    }

    triggerFilesSelection(event);
  };

  // Keyboard handlers to optimise a11y
  const handleAddFilesKeydown = (
    event: React.KeyboardEvent<HTMLDivElement>
  ) => {
    event.stopPropagation();

    const actionKeys = event.code == 'Space' || event.code == 'Enter';

    if (!actionKeys) return;

    handleAddFiles(event);
  };

  const addFilesToUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target) return;

    const files = Array.from(event.target.files as any).map((file: any) => {
      file.apiType = TransferItemAPITypes.file;
      file.relativePath = file.webkitRelativePath;

      return file;
    });

    dispatch(
      addFiles({
        transfer,
        files,
        allowedSize,
        currentSize,
        maximumNumberOfFiles,
      })
    );
  };

  // The user clicks elements in the transfer window, which will trigger these visually hidden inputs
  const renderFilesInput = () => {
    return (
      <input
        type="file"
        multiple
        aria-hidden="true"
        onChange={addFilesToUpload}
        ref={filesInputRef}
        className={styles.input}
        tabIndex={-1}
        data-testid="add-files-input"
      />
    );
  };

  const renderDirectoresInput = () => {
    return (
      <input
        type="file"
        ref={directoriesInputRef}
        aria-hidden="true"
        multiple
        webkitdirectory="webkitdirectory"
        // eslint-disable-next-line react/no-unknown-property
        directory="directory"
        onChange={addFilesToUpload}
        className={styles.input}
        tabIndex={-1}
      />
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <p className={styles.subtitle}>{I18n.t('page.transfer_in.title')}</p>
          <p className={styles.name}>{channelName}</p>
        </div>
        {/** TODO: Pass logo URL as a prop [TRA-6313]*/}
        <Image
          className={styles.avatar}
          src={avatar}
          alt="logo"
          width="48"
          height="48"
        />
      </div>
      <p className={styles.description}>
        {I18n.t('page.transfer_in.tooltip.text', {
          name: channelName,
        })}
      </p>
      {renderFilesInput()}
      {renderDirectoresInput()}
      <div
        className={styles.uploadContainer}
        onClick={handleAddFiles}
        onKeyDown={handleAddFilesKeydown}
        tabIndex={0}
      >
        <Icon
          type="PlusCircle"
          role="img"
          ariaLabel={I18n.t('transfer.form.add_files')}
          className={styles.plusCircle}
        />
        <p className={styles.addFiles}>
          {I18n.t('page.transfer_in.add_files')}
        </p>
        <button
          className={styles.addFolder}
          onClick={triggerDirectoriesSelection}
          data-type="folder"
        >
          {I18n.t('page.transfer_in.folder_upload')}
        </button>
      </div>
    </div>
  );
};
